import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './Properties.styles';
import { PropertiesItem } from '@/components/Ad/Properties/PropertiesItem';
import { useAdDetails } from '@/hooks/useAdDetails';
import { AdProperty } from '@/api/types/ads.types';
import { useServerDataContext } from '@/contexts';
import { getHiddenPropertyIds } from '@/utils/propertyVisibility';

function getSelectedOptionId(prop: AdProperty): string {
  if (prop.link) {
    const queryIndex = prop.link.indexOf('?');
    if (queryIndex >= 0) {
      const optionId = new URLSearchParams(prop.link.slice(queryIndex + 1)).get(
        `p[${prop.legacyResourceId}]`
      );
      if (optionId) return optionId;
    }
  }
  const rawValue = prop.originalValue?.value;
  return rawValue == null ? '' : String(rawValue);
}

export const Properties = () => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;

  const hiddenPropertyIds = useMemo(() => {
    const categoryProperties = adData?.category?.properties;
    if (!categoryProperties?.length) return [];

    const collectionValues: Record<string, unknown> = {};
    for (const prop of adData?.properties ?? []) {
      if (prop.legacyResourceId && prop.originalValue) {
        collectionValues[prop.legacyResourceId] = getSelectedOptionId(prop);
      }
    }

    return getHiddenPropertyIds(categoryProperties, collectionValues);
  }, [adData?.category?.properties, adData?.properties]);

  const cityProperty = {
    name: t('common.city'),
    value: adData?.region || t('common.no'),
    link: null,
  } as AdProperty;

  const properties =
    adData &&
    [cityProperty, ...adData.properties]
      .filter(property => property.value !== t('common.no'))
      .filter(
        property =>
          !property.legacyResourceId ||
          !hiddenPropertyIds.includes(property.legacyResourceId)
      );

  return (
    <S.PropertiesBox $itemsCount={properties ? properties.length : 4}>
      {adData
        ? properties?.map((property, index) => (
            <PropertiesItem
              key={`${index}-${property.name}`}
              adProperty={property}
            />
          ))
        : Array.from({ length: 4 }, (_, index) => (
            <PropertiesItem key={index} />
          ))}
    </S.PropertiesBox>
  );
};
